import { provideApplicationModulesDomain } from '@/domain/application-modules/application-modules.providers';
import { provideUsersDomain } from '@/domain/users/provide-users-domain';
import { provideThemingUi } from '@/libs/shared/ui/theming/lib/theming.providers';
import { DashboardingProvider } from '@/providers/dashboarding.provider';
import { DashboardsProvider } from '@/providers/dashboards/dashboards.provider';
import { DashboardsProviderAdapter } from '@/providers/dashboards/dashboards.provider.adapter';
import { SharedUiPaginatorIntlService } from '@/shared/ui/paginator';
import {
    SharedUiColorsService,
    SharedUiGapsService
} from '@/shared/ui/theming';
import { Environment } from '@/utils/environment/environment.interface';
import { I18nService } from '@/utils/i18n/i18n.service';
import { HttpBackend } from '@angular/common/http';
import {
    importProvidersFrom,
    inject,
    NgModule,
    provideAppInitializer
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import {
    BrowserModule,
    HAMMER_GESTURE_CONFIG,
    HammerGestureConfig
} from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import {
    TranslateLoader,
    TranslateModule,
    TranslateService,
    type TranslationObject
} from '@ngx-translate/core';
import {
    TRANSLATE_HTTP_LOADER_CONFIG,
    TranslateHttpLoader
} from '@ngx-translate/http-loader';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { SignalComponentIoModule } from 'ng-dynamic-component/signal-component-io';
import { DragulaModule } from 'ng2-dragula';
import { CookieService } from 'ngx-cookie-service';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material';
import { of, type Observable } from 'rxjs';
import { RosaCoreModule } from '../libs/rosa';
import { AuthProvider } from '../providers';
import { AppInitializationProvider } from '../providers/app-initialization/app-initialization.provider';
import { DocumentManagementSystemProvider } from '../providers/document-management-system/document-management-system.provider';
import { DocumentManagementSystemProviderAdapter } from '../providers/document-management-system/document-management-system.provider.adapter';
import { GlobalInfoProvider } from '../providers/global-info.provider';
import { HighchartsProvider } from '../providers/highcharts/highcharts.provider';
import { TailoredWidgetsProvider } from '../providers/tailored-widgets/tailored-widgets.provider';
import { TailoredWidgetsProviderAdapter } from '../providers/tailored-widgets/tailored-widgets.provider.adapter';
import { AdvizeoColorsService } from '../services/advizeo-colors.service';
import { AdvizeoGapsService } from '../services/advizeo-gaps.service';
import { MatPaginatorIntlCustom } from '../services/mat-paginator-intl-custom';
import { ReuseTaggedRoutesStrategy } from '../services/reuse-tagged-routes.strategy';
import { AngularRoutingService } from '../services/routing/angular-routing.service';
import { RoutingService } from '../services/routing/routing.service';
import { SharedUiPaginatorIntlServiceAdapter } from '../services/shared-ui-paginator-intl-service.adapter';
import { TutorialService } from '../services/tutorial.service';
import { AppStoreModule } from '../store/app-store.module';
import { AppRoutingModule } from './app-routing-module';
import { AppComponent } from './app.component';
import { provideBackend } from './backend_client';

class TranslateHttpLoaderWithNoneFallback extends TranslateHttpLoader {
    getTranslation(
        lang: string
    ): Observable<TranslationObject> {
        // Handling the 'none' case to allow for translation key display using the CTRL + ALT + L shortcut.
        // See the initializeKeyboardShortcuts method
        if (lang === 'none') {
            return of({});
        }
        return super.getTranslation(lang);
    }
}

export function initializeApp(
    appInitializationProvider: AppInitializationProvider,
    highchartsProvider: HighchartsProvider,
    authProvider: AuthProvider,
    i18n: I18nService,
    globalInfoProvider: GlobalInfoProvider,
    dashboardingProvider: DashboardingProvider,
    tutorialService: TutorialService
) {
    return () =>
        appInitializationProvider
            .checkBrowserCompatibility()
            .then(() =>
                appInitializationProvider.initializeApp()
            )
            .then(() => {
                appInitializationProvider.checkUrlCoherenceWithCookies();
            })
            .then(() => {
                highchartsProvider.init();
                authProvider.initializeAuthentication();
                i18n.initializeTranslations();
                globalInfoProvider.showGlobalInfoMessage();
                dashboardingProvider.initDashboarding();
                tutorialService.initTutorial();
            })
            .catch(error => {
                console.error(
                    'error during app initialization::',
                    error
                );

                appInitializationProvider.setFailureMessageInBootLoader();
                appInitializationProvider.setBootSpinnerVisibility(
                    false
                );
                throw error;
            });
}

@NgModule({
    declarations: [AppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        AppRoutingModule,
        AppStoreModule,
        RosaCoreModule,
        TranslateModule.forRoot({
            loader: [
                {
                    provide: TRANSLATE_HTTP_LOADER_CONFIG,
                    useValue: {
                        prefix: 'https://localization.advizeo.io/savee/',
                        suffix: '.json'
                    }
                },
                {
                    provide: TranslateLoader,
                    useClass:
                        TranslateHttpLoaderWithNoneFallback,
                    deps: [
                        HttpBackend,
                        TRANSLATE_HTTP_LOADER_CONFIG
                    ]
                }
            ]
        }),

        DragulaModule.forRoot(),
        AngularSvgIconModule.forRoot(),

        // MATERIAL
        MatSnackBarModule,
        MatDialogModule,
        MatNativeDateModule,

        NgxDaterangepickerMd.forRoot(),
        SignalComponentIoModule
    ],
    bootstrap: [AppComponent],
    providers: [
        CookieService,
        {
            provide: RoutingService,
            useExisting: AngularRoutingService
        },
        {
            provide: TailoredWidgetsProvider,
            useClass: TailoredWidgetsProviderAdapter
        },
        {
            provide: DocumentManagementSystemProvider,
            useClass:
                DocumentManagementSystemProviderAdapter
        },
        {
            provide: DashboardsProvider,
            useClass: DashboardsProviderAdapter
        },
        {
            provide: SharedUiColorsService,
            useExisting: AdvizeoColorsService
        },
        {
            provide: SharedUiGapsService,
            useExisting: AdvizeoGapsService
        },
        {
            provide: HAMMER_GESTURE_CONFIG,
            useClass: HammerGestureConfig
        },
        {
            provide: MatPaginatorIntl,
            useClass: MatPaginatorIntlCustom,
            deps: [TranslateService]
        },
        {
            provide: SharedUiPaginatorIntlService,
            useExisting: SharedUiPaginatorIntlServiceAdapter
        },
        {
            provide: RouteReuseStrategy,
            useExisting: ReuseTaggedRoutesStrategy
        },
        provideAppInitializer(() => {
            const initializerFn = initializeApp(
                inject(AppInitializationProvider),
                inject(HighchartsProvider),
                inject(AuthProvider),
                inject(I18nService),
                inject(GlobalInfoProvider),
                inject(DashboardingProvider),
                inject(TutorialService)
            );
            return initializerFn();
        }),
        {
            provide: Environment,
            useValue: (
                window as typeof window &
                    Record<string, string>
            )['APP_ENVIRONMENT']
        },
        importProvidersFrom(
            ReactiveFormsModule.withConfig({
                callSetDisabledState:
                    'whenDisabledForLegacyCode'
            })
        ),
        provideThemingUi(),
        provideUsersDomain(),
        provideBackend(),
        provideApplicationModulesDomain()
    ]
})
export class AppModule {}
